import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError, money } from "@/lib/api";

const slugify = (v) =>
  v.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").slice(0, 40);

export default function PartnerSignup() {
  const [plans, setPlans] = useState(null);
  const [selected, setSelected] = useState("partner");
  const [form, setForm] = useState({
    organization: "",
    slug: "",
    contact_name: "",
    email: "",
    password: "",
  });
  const [busy, setBusy] = useState(false);
  const [enterprise, setEnterprise] = useState({ students: "", note: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api
      .get("/public/partner-plans")
      .then(({ data }) => setPlans(data))
      .catch((e) => toast.error(apiError(e)));
  }, []);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/public/partner-signup", {
        ...form,
        slug: slugify(form.slug || form.organization),
        plan_code: selected,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiError(err));
      setBusy(false);
    }
  };

  const askEnterprise = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/partner/subscription/enterprise-inquiry", {
        organization: form.organization || "Prospective partner",
        contact_name: form.contact_name || "—",
        email: form.email || "no-reply@alabamachurchschool.com",
        student_count: Number(enterprise.students) || 0,
        note: enterprise.note,
      });
      toast.success(data.message);
      setEnterprise({ students: "", note: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-5 py-12" data-testid="partner-signup-page">
      <div className="mx-auto max-w-5xl">
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-900">
          ← Alabama Church School
        </Link>
        <h1 className="mt-4 font-display text-4xl font-bold text-slate-900 sm:text-5xl">
          Become an ACS educational partner
        </h1>
        <p className="mt-3 max-w-2xl text-base text-slate-600">
          One annual registration covers your organization's use of the ACS records portal — record
          uploads, ACS review, and issued academic documents for your families. Family enrollment
          fees are separate and paid by each family.
        </p>

        {!plans ? (
          <Loader2 className="mt-10 h-5 w-5 animate-spin text-slate-400" />
        ) : (
          <>
            <div className="mt-10 grid gap-4 lg:grid-cols-3">
              {plans.plans.map((p) => (
                <button
                  type="button"
                  key={p.code}
                  onClick={() => setSelected(p.code)}
                  className={`rounded-xl border bg-white p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md ${
                    selected === p.code
                      ? "border-slate-900 ring-1 ring-slate-900"
                      : "border-slate-200"
                  }`}
                  data-testid={`signup-plan-${p.code}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-900">{p.name}</span>
                    {selected === p.code && (
                      <CheckCircle2 className="h-4 w-4 text-slate-900" />
                    )}
                  </div>
                  <div className="mt-3 font-display text-3xl font-bold text-slate-900">
                    {money(p.price)}
                  </div>
                  <div className="text-xs text-slate-500">per year</div>
                  <div className="mt-3 text-sm text-slate-700">
                    Up to {p.student_limit.toLocaleString()} active students
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-10 grid gap-6 lg:grid-cols-2">
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <h2 className="text-base font-semibold text-slate-900">Your details</h2>
                  <form className="mt-4 space-y-4" onSubmit={submit}>
                    {[
                      ["organization", "Organization name", "text"],
                      ["contact_name", "Your name", "text"],
                      ["email", "Email (this becomes your login)", "email"],
                      ["password", "Choose a password (8+ characters)", "password"],
                    ].map(([key, label, type]) => (
                      <div key={key} className="space-y-2">
                        <Label>{label}</Label>
                        <Input
                          type={type}
                          required
                          minLength={key === "password" ? 8 : undefined}
                          value={form[key]}
                          onChange={(e) => set(key, e.target.value)}
                          data-testid={`signup-${key}`}
                        />
                      </div>
                    ))}
                    <div className="space-y-2">
                      <Label>Your enrollment web address</Label>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-500">/</span>
                        <Input
                          value={form.slug}
                          placeholder={slugify(form.organization) || "your-school"}
                          onChange={(e) => set("slug", slugify(e.target.value))}
                          data-testid="signup-slug"
                        />
                      </div>
                      <p className="text-xs text-slate-500">
                        Families will enroll at /{slugify(form.slug || form.organization) ||
                          "your-school"}
                      </p>
                    </div>
                    <Button
                      type="submit"
                      className="w-full gap-2"
                      disabled={busy}
                      data-testid="signup-submit"
                    >
                      {busy ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ArrowRight className="h-4 w-4" />
                      )}
                      Continue to payment
                    </Button>
                    <p className="text-xs text-slate-500">
                      Your partner login is created now and unlocks as soon as payment completes.
                    </p>
                  </form>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardContent className="space-y-4 p-6">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-slate-500" />
                    <h2 className="text-base font-semibold text-slate-900">
                      More than {plans.enterprise_above.toLocaleString()} students?
                    </h2>
                  </div>
                  <p className="text-sm text-slate-600">
                    Contact ACS for enterprise pricing — organizations this size are set up
                    personally rather than through self-serve signup.
                  </p>
                  <div className="space-y-2">
                    <Label>How many active students?</Label>
                    <Input
                      type="number"
                      value={enterprise.students}
                      onChange={(e) => setEnterprise({ ...enterprise, students: e.target.value })}
                      data-testid="signup-enterprise-students"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Anything we should know?</Label>
                    <Textarea
                      rows={3}
                      value={enterprise.note}
                      onChange={(e) => setEnterprise({ ...enterprise, note: e.target.value })}
                      data-testid="signup-enterprise-note"
                    />
                  </div>
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={askEnterprise}
                    disabled={sending}
                    data-testid="signup-enterprise-submit"
                  >
                    {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                    Contact ACS for enterprise pricing
                  </Button>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
